import { useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getItems } from '../../services'
import { Item } from '../../types/response'
import { paths } from '@/utils/path'
import { Badge } from '@/components/ui/badge'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import Paper from '@/components/Paper'
import {
    EQUIPMENT_STATUS_VARIANTS,
    ITEM_CONDITION_VARIANTS,
} from '../../constants'

const locationItemColumns: DataTableColumn<Item>[] = [
    {
        key: 'item_type',
        title: 'Type',
        width: 120,
        render: (_value, row) => (
            <Badge
                variant={
                    row.item_type === 'equipment' ? 'default' : 'secondary'
                }
                className="capitalize"
            >
                {row.item_type}
            </Badge>
        ),
    },
    {
        key: 'name',
        title: 'Name',
        width: 250,
        pinned: 'left',
        render: (_value, row) => {
            const path =
                row.item_type === 'equipment'
                    ? `${paths.warehouseEquipment}/${row.id}`
                    : `${paths.warehouseComponents}/${row.id}`
            return (
                <Link
                    to={path}
                    className="text-blue-600 hover:underline font-medium"
                >
                    {row.name}
                </Link>
            )
        },
    },
    {
        key: 'service_tag',
        title: 'Service Tag / Ref',
        width: 170,
        render: (_value, row) => (
            <span className="text-sm">{row.service_tag || row.ref || '-'}</span>
        ),
    },
    {
        key: 'category',
        title: 'Category',
        width: 130,
        render: (value) => (
            <span className="text-sm capitalize">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'status',
        title: 'Status',
        width: 130,
        render: (_value, row) => (
            <Badge
                variant={
                    (EQUIPMENT_STATUS_VARIANTS[row.status] as
                        | 'default'
                        | 'secondary'
                        | 'outline'
                        | 'destructive') || 'outline'
                }
                className="capitalize"
            >
                {row.status?.replace('_', ' ')}
            </Badge>
        ),
    },
    {
        key: 'condition',
        title: 'Condition',
        width: 120,
        render: (_value, row) => (
            <Badge
                variant={
                    (ITEM_CONDITION_VARIANTS[row.condition] as
                        | 'default'
                        | 'secondary'
                        | 'outline'
                        | 'destructive') || 'outline'
                }
                className="capitalize"
            >
                {row.condition}
            </Badge>
        ),
    },
    {
        key: 'owner',
        title: 'Owner',
        width: 130,
        render: (value) => (
            <span className="text-sm">{(value as string) || '-'}</span>
        ),
    },
    {
        key: 'supplier',
        title: 'Supplier',
        width: 180,
        render: (_value, row) => (
            <span className="text-sm">{row.supplier?.name || '-'}</span>
        ),
    },
]

/**
 * LocationItems - Displays items at a specific location
 * Used in LocationDetail tabs
 */
const LocationItems = () => {
    const { id: locationId } = useParams<{ id: string }>()

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            prefix: 'item',
            defaults: { sort_by: 'created_at', sort_order: 'desc' },
        })

    const fetchFn = useCallback(
        () => getItems({ ...queryParams, location_id: locationId }),
        [queryParams, locationId]
    )

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.WAREHOUSE_ITEMS,
            'location',
            locationId,
            queryParams,
        ],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const items = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => locationItemColumns, [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return (
        <Paper className="mt-2 space-y-2">
            {renderState() || (
                <DataTableCustomized
                    columns={columns}
                    rows={items}
                    rowKey={(row) => row.id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: 'Search items...',
                        },
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default LocationItems
